import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Check as CheckIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAvailablePlans, useCurrentPlan, useCreateCheckoutSession, useSubscriptionStatus, useBillingPortal } from '../hooks/useCommon';
import { useAuth } from '../hooks/useAuth';
import { styled } from '@mui/material/styles';

const PlanCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const PopularBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
  color: 'white',
  fontWeight: 600,
}));

const PriceTypography = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}));

interface Plan {
  id: number;
  name: string;
  description: string | null;
  price: number;
  monthly_limit: number | null;
  daily_limit: number | null;
  business_profiles_limit: number | null;
  stripe_price_id: string | null;
  features: string[] | null;
}

interface PlansDisplayProps {
  onPlanSelected?: (planId: number) => void;
}

const PlansDisplay: React.FC<PlansDisplayProps> = ({ onPlanSelected }) => {
  const { t } = useTranslation('billing');
  const { user } = useAuth();
  const { data: plans, isLoading, error } = useAvailablePlans();
  const { data: currentPlan } = useCurrentPlan(user?.id);
  const { data: subscriptionStatus } = useSubscriptionStatus(user?.id);
  const createCheckoutSession = useCreateCheckoutSession();
  const billingPortal = useBillingPortal();

  const hasActiveSubscription = subscriptionStatus && 
    (subscriptionStatus.status === 'active' || subscriptionStatus.status === 'trialing');

  const handleUpgrade = async (plan: Plan) => {
    // Don't allow checkout for free plans
    if (plan.price === 0) {
      return;
    }

    if (onPlanSelected) {
      onPlanSelected(plan.id);
    }

    // If user has active subscription, route to Billing Portal instead of Checkout
    if (hasActiveSubscription) {
      try {
        await billingPortal.mutateAsync();
      } catch (error) {
        // Error is handled by the mutation's onError
      }
      return;
    }

    // Only use Checkout for users without active subscription (free tier)
    try {
      await createCheckoutSession.mutateAsync(plan.id);
    } catch (error) {
      // Error is handled by the mutation's onError
    }
  };

  const formatLimit = (limit: number | null) => {
    if (limit === null) return t('plans.unlimited');
    if (limit === -1) return t('plans.unlimited');
    return limit.toLocaleString();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {t('plans.loadError')}
      </Alert>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <Alert severity="info">
        {t('plans.noPlans')}
      </Alert>
    );
  }

  const isCurrentPlan = (plan: Plan) => {
    return currentPlan?.id === plan.id;
  };

  // Order plans: Free, Starter, Pro, Agency
  const planOrder = ['Free', 'Starter', 'Pro', 'Agency'];
  const sortedPlans = [...(plans || [])].sort((a, b) => {
    const aIndex = planOrder.indexOf(a.name);
    const bIndex = planOrder.indexOf(b.name);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  return (
    <Box>
      <Grid container spacing={3}>
        {sortedPlans.map((plan: Plan) => {
        const current = isCurrentPlan(plan);
        const isFree = plan.price === 0;
        const isPopular = plan.name.toLowerCase() === 'pro';
        const isAgency = plan.name.toLowerCase() === 'agency';

        return (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <PlanCard
              sx={{
                border: current ? '2px solid #8b9cff' : '1px solid',
                borderColor: current ? '#8b9cff' : 'divider',
              }}
            >
              {isPopular && <PopularBadge label={t('plans.popular')} size="small" />}
              
              <CardContent sx={{ flexGrow: 1, pt: 4, display: 'flex', flexDirection: 'column' }}>
                <Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                    {plan.name}
                  </Typography>
                  
                  {plan.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, minHeight: 40 }}>
                      {t(`plans.descriptions.${plan.name.toLowerCase()}`, { defaultValue: plan.description })}
                    </Typography>
                  )}

                  <Box sx={{ mb: 3 }}>
                    <PriceTypography>
                      ${plan.price.toFixed(2)}
                    </PriceTypography>
                    <Typography variant="body2" color="text.secondary">
                      {t('plans.perMonth')}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">
                        {formatLimit(plan.monthly_limit)} {t('plans.monthlyGenerations')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">
                        {formatLimit(plan.daily_limit)} {t('plans.dailyGenerations')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: plan.features && plan.features.length > 0 ? 2 : 0 }}>
                      <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                      <Typography variant="body2">
                        {formatLimit(plan.business_profiles_limit)} {plan.business_profiles_limit !== 1 ? t('plans.businessProfiles') : t('plans.businessProfile')}
                      </Typography>
                    </Box>
                    
                    {/* Features from database - capped at 6 items */}
                    {plan.features && plan.features.length > 0 && (
                      <>
                        {plan.features.slice(0, 6).map((feature, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                            <CheckIcon sx={{ color: 'success.main', mr: 1, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                        {plan.features.length > 6 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {t('plans.andMore')}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant={current ? 'outlined' : 'contained'}
                  color="primary"
                  onClick={() => handleUpgrade(plan)}
                  disabled={current || isFree || isAgency || (hasActiveSubscription ? billingPortal.isPending : createCheckoutSession.isPending)}
                  startIcon={
                    (hasActiveSubscription ? billingPortal.isPending : createCheckoutSession.isPending) ? (
                      <CircularProgress size={20} />
                    ) : hasActiveSubscription ? (
                      <SettingsIcon />
                    ) : (
                      <CreditCardIcon />
                    )
                  }
                  sx={{
                    mt: 'auto',
                    background: current || isFree || isAgency ? 'transparent' : 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    '&:hover': {
                      background: current || isFree || isAgency ? 'transparent' : 'linear-gradient(135deg, #5a6fd8 0%, #d94f7e 100%)',
                    },
                  }}
                >
                  {current
                    ? t('plans.currentPlan')
                    : isFree
                    ? t('plans.freePlan')
                    : isAgency
                    ? t('plans.comingSoon')
                    : hasActiveSubscription && currentPlan && currentPlan.price > 0 && plan.price > currentPlan.price
                    ? t('plans.upgradeTo', { name: plan.name })
                    : hasActiveSubscription
                    ? t('plans.changeTo', { name: plan.name })
                    : t('plans.upgradeTo', { name: plan.name })}
                </Button>
              </CardContent>
            </PlanCard>
          </Grid>
        );
      })}
      </Grid>
    </Box>
  );
};

export default PlansDisplay;

